import { useRef, useState } from "react";
import { parseFile, parseFiles } from "../modules/parser";
import "./FileUpload.css";

/*
  FileUpload.jsx — File upload component

  Behavior varies by mode:
  - "vessel" : single file, has vessel target, safe for large files
  - "explore": single file, no vessel target, warns on large files
  - "multi"  : multiple files (Pro), vessel target required
*/

// Warn if file exceeds this size in explore mode (50MB)
const EXPLORE_WARN_BYTES = 50 * 1024 * 1024;

export default function FileUpload({
  setAllRecords,
  setAllRecordsMulti,
  setStatus,
  setLoading,
  setPercent,
  unlocked,
  vesselTargets,
  mode,
  allowMultiFile,
}) {
  const [dragging, setDragging] = useState(false);
  const [showLargeFileWarning, setShowLargeFileWarning] = useState(false);
  const [pendingFiles, setPendingFiles] = useState(null);
  const fileInputRef = useRef(null);

  async function handleFiles(files) {
    if (!files || files.length === 0) return;

    // In explore mode, warn before parsing large files
    if (mode === "explore") {
      const file = files[0];
      if (file.size > EXPLORE_WARN_BYTES) {
        setPendingFiles(files);
        setShowLargeFileWarning(true);
        return;
      }
    }

    await processFiles(files);
  }

  async function processFiles(files) {
    setShowLargeFileWarning(false);
    setPendingFiles(null);

    if (mode === "multi") {
      await handleMultiMode(files);
    } else {
      await handleSingleMode(files);
    }
  }

  async function handleSingleMode(files) {
    const file = files[0];
    if (!file.name.toLowerCase().endsWith(".csv")) {
      setStatus("Please upload a .csv file.");
      return;
    }

    setLoading(true);
    setPercent(0);

    const hasFilter = vesselTargets && vesselTargets.length > 0;
    setStatus(
      hasFilter
        ? `Parsing ${file.name} — searching for your vessel...`
        : `Parsing ${file.name} — loading all vessels...`
    );

    try {
      const records = await parseFile(file, hasFilter ? vesselTargets : null, (pct, count) => {
        setPercent(pct);
        if (pct >= 95) {
          setStatus(`Finalizing — ${count.toLocaleString()} records found...`);
        } else {
          setStatus(
            hasFilter
              ? `Parsing ${file.name} — ${count.toLocaleString()} matching records...`
              : `Parsing ${file.name} — ${count.toLocaleString()} records loaded...`
          );
        }
      });

      setPercent(null);
      if (records.length === 0) {
        setStatus(
          hasFilter
            ? "No records found for that vessel. Check the MMSI or name and try again."
            : "No valid AIS records found. Check that this is a standard AIS CSV."
        );
        setAllRecords([]);
      } else {
        setStatus(`Done — ${records.length.toLocaleString()} records loaded. See the map below.`);
        setAllRecords(records);
      }
    } catch (error) {
      setPercent(null);
      setStatus(`Error reading file: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }

  async function handleMultiMode(files) {
    const csvFiles = Array.from(files).filter((f) => f.name.toLowerCase().endsWith(".csv"));
    if (csvFiles.length === 0) {
      setStatus("Please upload .csv files.");
      return;
    }

    if (!vesselTargets || vesselTargets.length === 0) {
      setStatus("Please enter at least one vessel name or MMSI before uploading multiple files.");
      return;
    }

    setLoading(true);
    setPercent(0);

    try {
      const results = await parseFiles(
        csvFiles,
        vesselTargets,
        (fileIndex, total, name, pct, count) => {
          setPercent(pct);
          const label = pct >= 95 ? "Finalizing" : "Parsing";
          setStatus(
            `${label} file ${fileIndex + 1} of ${total}: ${name} — ${count.toLocaleString()} records...`
          );
        }
      );

      setPercent(null);
      const totalRecords = results.reduce((sum, r) => sum + r.length, 0);

      if (totalRecords === 0) {
        setStatus("No records found for that vessel across the uploaded files.");
        setAllRecordsMulti([[]]);
      } else {
        setStatus(
          `Done — ${totalRecords.toLocaleString()} records across ${csvFiles.length} file${csvFiles.length > 1 ? "s" : ""}. See the map below.`
        );
        setAllRecordsMulti(results);
      }
    } catch (error) {
      setPercent(null);
      setStatus(`Error reading files: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }

  function onDragOver(e) {
    e.preventDefault();
    setDragging(true);
  }

  function onDragLeave(e) {
    // Only clear dragging if we're leaving the drop zone entirely, not a child element
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragging(false);
    }
  }

  function onDrop(e) {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  }

  function onFileInputChange(e) {
    handleFiles(e.target.files);
    e.target.value = "";
  }

  const stepLabel = mode === "explore" ? "Step 1" : "Step 2";
  const dropLabel = mode === "multi"
    ? "Drag and drop one or more AIS CSV files here"
    : "Drag and drop your AIS CSV here";

  return (
    <div className="upload-section">
      <h2 className="section-title">
        {stepLabel} — Upload your AIS file{mode === "multi" ? "s" : ""}
      </h2>
      <p className="section-hint">
        Works with NOAA, MarineTraffic, Spire, or any standard AIS CSV.{" "}
        <a
          href="https://coast.noaa.gov/htdata/CMSP/AISDataHandler/2024/index.html"
          target="_blank"
          rel="noreferrer"
        >
          Download NOAA data here.
        </a>
        {mode === "multi" && " Upload multiple files to consolidate across date ranges."}
      </p>

      {/* Large file warning — explore mode only */}
      {showLargeFileWarning && (
        <div className="large-file-warning">
          <p className="large-file-warning-title">⚠ Large file detected</p>
          <p className="large-file-warning-desc">
            This file is large and may be slow or cause your browser to freeze.
            For best results, switch to "I know my vessel" mode and enter an MMSI or vessel name first.
          </p>
          <div className="large-file-warning-actions">
            <button
              className="large-file-warning-btn large-file-warning-btn--proceed"
              onClick={() => processFiles(pendingFiles)}
            >
              Proceed anyway
            </button>
            <button
              className="large-file-warning-btn large-file-warning-btn--cancel"
              onClick={() => { setShowLargeFileWarning(false); setPendingFiles(null); }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div
        className={`drop-zone ${dragging ? "drop-zone--dragging" : ""}`}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={() => { if (!showLargeFileWarning) fileInputRef.current.click(); }}
      >
        <p className="drop-zone-text">
          {dragging ? "Release to upload" : dropLabel}
        </p>
        <p className="drop-zone-subtext">or click to browse</p>

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          multiple={allowMultiFile}
          style={{ display: "none" }}
          onChange={onFileInputChange}
        />
      </div>
    </div>
  );
}
